/** 客诉状态（与后端 ComplaintStatus 对应） */
export enum ComplaintStatus {
    PENDING_DSP_ACTION = 'PENDING_DSP_ACTION',
    APPEALING = 'APPEALING',
    REVOKED = 'REVOKED',
    ENFORCED = 'ENFORCED',
    ACKNOWLEDGED = 'ACKNOWLEDGED',
    TIMEOUT = 'TIMEOUT',
    CLOSED = 'CLOSED',
}

/** DSP 端三 Tab（前端 UI） */
export enum CustomerComplaintTab {
    PENDING = 'PENDING',         // status = PENDING_DSP_ACTION
    APPEALING = 'APPEALING',     // status = APPEALING
    CLOSED = 'CLOSED',           // status ∈ 终态（REVOKED/ENFORCED/ACKNOWLEDGED/TIMEOUT/CLOSED）
}

/**
 * Tab → 后端列表/计数查询参数。
 * 未完结类（待处理/申诉中）：isFinished=0 + 单 status，命中 idx_wh_dsp_unfinished。
 * 已完结类（已结案）：isFinished=1、不传 statusList，命中 idx_wh_dsp_finished（status 不在该索引中，带上冗余）。
 */
export const TAB_TO_QUERY_PARAMS: Record<
    CustomerComplaintTab,
    {isFinished: number; statusList?: string[]}
> = {
    [CustomerComplaintTab.PENDING]: {isFinished: 0, statusList: [ComplaintStatus.PENDING_DSP_ACTION]},
    [CustomerComplaintTab.APPEALING]: {isFinished: 0, statusList: [ComplaintStatus.APPEALING]},
    [CustomerComplaintTab.CLOSED]: {isFinished: 1},
};

/** 客诉记录（GraphQL CustomerComplaintType） */
export interface CustomerComplaint {
    id: number;
    complaintCategory: string;
    complaintType: string;
    /** 客诉原因结构化解析（含原文+三语译文）；前端按 locale 取译文、可切原文；富文本经净化展示 */
    complaintReason?: AppealReasonParsed | null;
    /** 客诉发起渠道：EMAIL/PHONE/OTHER */
    source?: string;
    externalRefId?: string;
    /** 外部单据业务编码（人类可读），仅展示用 */
    externalRefCode?: string;
    driverId: number | null;
    dspId: number | null;
    orderId?: number;
    hubId?: number;
    warehouseId?: number;
    /** 证据文件列表JSON：[{"file_name":"xxx.jpg","file_type":"jpg","file_url":"https://..."}] */
    evidenceFilesJson?: string;
    status: string;
    /** 完结态标志：0=进行中；1=已完结 */
    isFinished: number;
    /** DSP 申诉/处理截止时间（epoch seconds） */
    appealDeadlineAt?: number;
    /** 完结时间（epoch seconds；任意完结态发生时刻；通过 status 区分类型） */
    finishedAt?: number;
    /** epoch seconds */
    createdAt?: number;
    /** epoch seconds */
    updatedAt?: number;
    /** DSP 申诉提交时间（epoch seconds；仅 APPEALING 及后续状态有值） */
    appealSubmittedAt?: number;
    /** 关联运单号，无关联订单时为 null */
    trackingNumber?: string;
    /** 仓库名称（列表 enrich 拼装；详情接口可为 null） */
    warehouseName?: string;
    /** 所属 DSP 名称；dspId 为空或 DSP 已删除时为 null */
    dspName?: string;
    /** 派送司机姓名（列表 enrich 拼装；详情接口可为 null） */
    driverName?: string;
    /** 关联订单的当前 overallStatus；order 已删除或查询失败时为 null */
    orderOverallStatus?: string;
    /** POD 图 URL 列表（后端按 key 拼预签名 URL，供 DSP 申诉时核验妥投凭证） */
    podImageS3UrlsJson?: string[];
    /** 最后操作人姓名；updatedBy 为 0(系统)/账号查不到时为 null，前端兜底为「系统」 */
    operatorName?: string | null;
    /**
     * 收件人信息（均取自订单的收件人地址，供核验投递地址是否正确）。
     * 字段口径与订单侧 OrderAddressType 保持一致。
     * 订单已删除 / 收件人地址缺失 / 地址未解析出坐标时为 null，展示侧统一兜底为「-」。
     */
    /** 原始地址：客户下单时填写、未经清洗 */
    recipientAddress?: string | null;
    /** 标准化地址：地址服务清洗后、派送实际依据；未标准化时为 null，展示侧回退 recipientAddress */
    recipientStandardizedAddress?: string | null;
    recipientName?: string | null;
    /** 电话区号与号码分开返回（与订单侧同构），展示侧自行组装 */
    recipientPhoneCountryCode?: number | null;
    recipientPhoneNumber?: string | null;
    recipientLatitude?: number | null;
    recipientLongitude?: number | null;
}

export interface AppealReasonParsed {
    text: string;
    lang: string;
    translations: Array<{lang: string; text: string}>;
}

/** 客诉申诉详情（GraphQL ComplaintAppealDetailType；ext 瘦身后只含申诉内容+审核结果） */
export interface ComplaintAppealDetail {
    id: number;
    complaintId: number;
    reviewTaskId?: number;
    /** 后端结构化解析的申诉理由；前端按 i18n.language 选 translations[locale] ?? text */
    appealReason?: AppealReasonParsed | null;
    /** 申诉附件预签名 URL 列表，由后端按 s3 keys 拼装；前端直接展示 */
    appealAttachmentS3UrlsJson?: string[];
    /** epoch seconds */
    appealSubmittedAt?: number;
    appealSubmittedBy?: number;
    appealSubmitterName?: string;
    reviewResult?: string;
    reviewerId?: number;
    /** 后端结构化解析的审核评论，前端无需自行 JSON.parse；入库 JSON 为空/解析失败时为 null */
    reviewComment?: ReviewCommentParsed | null;
    /** epoch seconds */
    reviewedAt?: number;
}

export interface ReviewCommentTranslationEntry {
    /** BCP-47 short code：zh|en|es */
    lang: string;
    /** 该语言下的翻译文本 */
    text: string;
}

export interface ReviewRejectReasonOptionInline {
    /** 字典 code，与 review_comment_json.tagKeys 中的值一致 */
    code: string;
    /** 展示顺序 */
    displayOrder?: number;
    /** 可扩展多语言文案，按 locale 取 */
    texts: {lang: string; text: string}[];
}

export interface ReviewCommentParsed {
    /** 标签 code 列表（与 ReviewRejectReasonOption.code 对齐） */
    tagKeys: string[];
    /** 按 tagKeys 顺序解析后的字典项；前端展示直接按 locale 从 texts 取 */
    tagOptions: ReviewRejectReasonOptionInline[];
    /** 自由文本原文 */
    text: string;
    /** 自由文本源语言 */
    lang: string;
    /** 自由文本翻译列表 */
    translations: ReviewCommentTranslationEntry[];
}

/** 列表分页 */
export interface ComplaintListPage {
    content: CustomerComplaint[];
    totalElements: number;
    page: number;
    size: number;
}

/** 通用分页请求（对应后端 PageRequestType） */
export interface PageRequestTypeInput {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: 'ASC' | 'DESC';
}

/**
 * DSP 问题件列表筛选条件（对应后端 CustomerComplaintListFilterInput）。
 *
 * dspId / warehouseId 是筛选维度的一部分：
 *   - DSP 角色：dspId 由前端 useCurrentDsp 注入，默认携带不可修改
 *   - HQ / Warehouse Manager（一期 PC 端开放）：dspId 可选
 *   - warehouseId 全场景必填，与表索引最左前缀对齐
 */
export interface CustomerComplaintListFilter {
    dspId?: number;
    warehouseId: number;
    /** 客诉状态列表；空表示不限 */
    statusList?: string[];
    /** 完结态过滤：undefined=不限，0=仅未完结(待处理/申诉中)，1=仅已完结(已结案)；与后端 is_finished 复合索引对齐 */
    isFinished?: number;
    /** 运单号筛选，原始字符串；支持以 ; , 换行 分隔多值，后端拆分后 IN 查询 */
    trackingNumbers?: string;
    driverId?: number;
    complaintType?: string;
    /** 客诉发起渠道（EMAIL/PHONE/OTHER）；多值 IN 查询，单值场景传 1 元素列表即可 */
    sourceList?: string[];
    /** ISO-8601 UTC 字符串 */
    createdAtFrom?: string;
    createdAtTo?: string;
    appealSubmittedAtFrom?: string;
    appealSubmittedAtTo?: string;
    /** 完结时间区间（任意终态发生时刻）；ISO-8601 UTC */
    finishedAtFrom?: string;
    finishedAtTo?: string;
    /** 排序字段白名单：APPEAL_DEADLINE_AT（默认）/FINISHED_AT/CREATED_AT */
    sortBy?: 'APPEAL_DEADLINE_AT' | 'FINISHED_AT' | 'CREATED_AT';
    /** 排序方向：ASC / DESC */
    sortDirection?: 'ASC' | 'DESC';
}

/**
 * QC 总部（HQ）视角 DSP 申诉汇总筛选条件（对应后端 CustomerComplaintHqListFilterInput）。
 *
 * 与 CustomerComplaintListFilter 的关键区别在 warehouseId 的**语义**：
 * 那里是 DSP / wms 的单仓越权边界（必填、由后端强制校验），这里只是可选筛选维度（空=全美）。
 * 因语义不同故独立成类型，而非放开原类型的非空约束——否则 DSP 账号可构造出拉全美的请求。
 * 时间范围必填（createdAt 或 finishedAt 至少一组），后端亦强制校验（MISSING_FIELD）。
 */
export interface CustomerComplaintHqListFilter {
    /** DSP ID；不带表示全部 DSP */
    dspId?: number;
    /** 仓库 ID；不带表示全美 */
    warehouseId?: number;
    /** 客诉状态列表；空表示不限 */
    statusList?: string[];
    /** 完结态过滤：undefined=不限，0=仅未完结(待处理/申诉中)，1=仅已完结(已结案) */
    isFinished?: number;
    /** 运单号筛选，原始字符串；支持以 ; , 换行 分隔多值，后端拆分后 IN 查询 */
    trackingNumbers?: string;
    driverId?: number;
    complaintType?: string;
    /** 客诉发起渠道（EMAIL/PHONE/OTHER）；多值 IN 查询 */
    sourceList?: string[];
    /** ISO-8601 UTC 字符串；与 finishedAt 至少一组必填 */
    createdAtFrom?: string;
    createdAtTo?: string;
    appealSubmittedAtFrom?: string;
    appealSubmittedAtTo?: string;
    /** 完结时间区间；ISO-8601 UTC */
    finishedAtFrom?: string;
    finishedAtTo?: string;
    /** 排序字段白名单：APPEAL_DEADLINE_AT（默认）/FINISHED_AT/CREATED_AT */
    sortBy?: 'APPEAL_DEADLINE_AT' | 'FINISHED_AT' | 'CREATED_AT';
    /** 排序方向：ASC / DESC */
    sortDirection?: 'ASC' | 'DESC';
}

/**
 * 问题件工作台运行视角。
 *
 *  - DSP：dspId 由 useCurrentDsp 注入，warehouseId 来自顶部仓库选择器，可提交申诉 / 放弃 / 认责
 *  - wms：仓库角色，warehouseId 来自顶部仓库选择器，仅查看
 *  - HQ ：QC 总部全美视角，无 warehouseId、无仓库选择器，时间范围必填，仅查看
 */
export type WorkbenchMode = 'DSP' | 'wms' | 'HQ';
